import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { CreditCard, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(amount) { return '₹' + amount.toLocaleString('en-IN'); }

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtCoverage(from, until) {
  return `${fmtDate(from)} – ${fmtDate(until)}`;
}

function DueDateCard({ nextDue }) {
  if (!nextDue) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-3xl p-5 flex items-center gap-4">
        <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center shrink-0">
          <CreditCard className="w-6 h-6 text-gray-400" strokeWidth={1.5} />
        </div>
        <div>
          <p className="font-heading font-medium text-[#292524]">No payment on record</p>
          <p className="text-xs text-[#78716C] font-body mt-0.5">Contact Meena to record your first payment.</p>
        </div>
      </div>
    );
  }

  const dueDate = new Date(nextDue + 'T00:00:00');
  const todayDate = new Date(); todayDate.setHours(0,0,0,0);
  const diffDays = Math.round((dueDate - todayDate) / (1000 * 60 * 60 * 24));

  let color, icon, label, sublabel;
  if (diffDays < 0) {
    color = 'bg-red-50 border-red-300';
    icon = <AlertTriangle className="w-6 h-6 text-red-500" strokeWidth={1.5} />;
    label = 'Payment Overdue';
    sublabel = `Was due ${fmtDate(nextDue)} (${Math.abs(diffDays)} days ago)`;
  } else if (diffDays <= 7) {
    color = 'bg-orange-50 border-orange-300';
    icon = <Clock className="w-6 h-6 text-orange-500" strokeWidth={1.5} />;
    label = 'Due Soon';
    sublabel = `${fmtDate(nextDue)} — in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  } else {
    color = 'bg-green-50 border-green-200';
    icon = <CheckCircle className="w-6 h-6 text-green-500" strokeWidth={1.5} />;
    label = 'Next Payment Due';
    sublabel = `${fmtDate(nextDue)} — in ${diffDays} days`;
  }

  return (
    <div data-testid="next-due-card" className={`border rounded-3xl p-5 flex items-center gap-4 ${color}`}>
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-white/60 shrink-0">{icon}</div>
      <div>
        <p className="font-heading font-medium text-[#292524]">{label}</p>
        <p className="text-sm text-[#57534E] font-body mt-0.5">{sublabel}</p>
      </div>
    </div>
  );
}

const PLAN_LABELS = { 1: '1 Month', 4: '4 Months', 6: '6 Months' };
const PLAN_COLORS = {
  1: 'bg-green-100 text-green-700',
  4: 'bg-pink-100 text-pink-700',
  6: 'bg-purple-100 text-purple-700',
};

export default function StudentPayments() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/student/payments`, { headers: getAuthHeaders() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">My Fees</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Payments</h1>
        <p className="text-[#57534E] font-body mt-1">Track your fee payments and upcoming dues.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Next due date card */}
          <DueDateCard nextDue={data?.next_due} />

          {/* Summary */}
          {data?.payments?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 text-center">
                <p className="font-heading text-xl font-semibold text-[#292524]">{data.payments.length}</p>
                <p className="text-xs text-[#78716C] font-body mt-0.5">Total Payments</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 text-center">
                <p className="font-heading text-xl font-semibold text-[#292524]">
                  ₹{data.payments.reduce((s, p) => s + p.amount, 0).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-[#78716C] font-body mt-0.5">Total Paid</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50 text-center col-span-2 sm:col-span-1">
                <p className="font-heading text-xl font-semibold text-[#292524] capitalize">{data.payments[0]?.mode || '—'}</p>
                <p className="text-xs text-[#78716C] font-body mt-0.5">Last Mode</p>
              </div>
            </div>
          )}

          {/* Payment history */}
          {data?.payments?.length === 0 ? (
            <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
              <CreditCard className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-heading font-medium text-[#292524]">No payment history yet</p>
              <p className="text-[#78716C] text-sm font-body mt-1">Your payments will appear here once recorded by Meena.</p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E7E5E4]/60">
                <h2 className="font-heading font-medium text-[#292524] text-base">Payment History</h2>
              </div>
              <div className="divide-y divide-[#E7E5E4]/40">
                {data.payments.map((p, i) => (
                  <div key={p.id || i} data-testid={`payment-history-${i}`} className="px-5 py-4 flex items-start gap-4">
                    <div className={`mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${PLAN_COLORS[p.plan_months] || 'bg-gray-100'}`}>
                      <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-heading font-semibold text-[#292524] text-sm">{fmt(p.amount)}</p>
                          <p className="text-xs text-[#78716C] font-body mt-0.5">{fmtCoverage(p.valid_from, p.valid_until)}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`inline-block text-xs font-heading font-medium px-2.5 py-1 rounded-full ${PLAN_COLORS[p.plan_months] || 'bg-gray-100 text-gray-700'}`}>
                            {PLAN_LABELS[p.plan_months] || `${p.plan_months} months`}
                          </span>
                          <p className="text-xs text-[#A8A29E] font-body mt-1 capitalize">{p.mode}</p>
                        </div>
                      </div>
                      <p className="text-xs text-[#A8A29E] font-body mt-1">Paid on {fmtDate(p.payment_date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
